import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Wine, Trophy, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/dc620d9a-df93-4c4f-8641-194131d4cd35/images/d4f6606dc1b8ac82ecf641ac50068922ad38929db833ccda2657e2549b1aa81a.png";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (user && user.id) nav("/feed");
  }, [user, nav]);

  return (
    <div className="min-h-screen relative overflow-hidden grain bg-[#0a0a0a]">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
      </div>

      {/* Top nav */}
      <header className="relative z-20 flex items-center justify-between max-w-6xl mx-auto px-6 sm:px-10 py-6">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full border border-[#d4af37]/70 flex items-center justify-center">
            <span className="text-[#d4af37] font-serif text-lg">L</span>
          </span>
          <span className="font-serif text-2xl tracking-wider text-white/90">Lumière Club</span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-login"
            className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/70 hover:text-[#d4af37] transition"
          >
            Sign In
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-12 sm:pt-24 pb-16">
        <div className="max-w-3xl fade-up">
          <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em] mb-6" data-testid="hero-overline">
            By invitation · Malaysia
          </p>
          <h1 className="font-serif text-5xl sm:text-7xl leading-[1.05] tracking-tight text-white/95">
            A members-only<br />
            <span className="text-[#d4af37] italic">salon</span> for those who
            <br />
            taste the finer things.
          </h1>
          <p className="mt-8 text-base sm:text-lg text-white/60 max-w-xl leading-relaxed">
            Lumière Club is a private digital society for Malaysia's young connoisseurs — pickleball,
            golf, cigars, wine, and yoga. Stories vanish in 24 hours. Access is granted by referral and
            curation.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/apply"
              data-testid="apply-cta"
              className="group relative inline-flex items-center justify-center gap-3 bg-[#d4af37] text-black px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium hover:bg-[#f3e5ab] transition"
            >
              <span className="relative z-10">申请加入 · Apply</span>
              <ArrowRight size={16} strokeWidth={2} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              <span className="absolute -inset-4 bg-[#d4af37]/30 blur-2xl rounded-full -z-0" />
            </Link>
            <Link
              to="/login"
              data-testid="signin-cta"
              className="inline-flex items-center justify-center gap-3 border border-white/20 hover:border-[#d4af37]/60 text-white/80 hover:text-white px-8 py-4 text-sm uppercase tracking-[0.2em] transition"
            >
              Members Sign In
            </Link>
          </div>

          <div className="mt-16 flex flex-wrap gap-x-10 gap-y-4 text-white/40 text-xs uppercase tracking-[0.25em]">
            <span className="flex items-center gap-2">
              <Wine size={14} strokeWidth={1.5} className="text-[#d4af37]" /> Curated Tastings
            </span>
            <span className="flex items-center gap-2">
              <Trophy size={14} strokeWidth={1.5} className="text-[#d4af37]" /> Private Events
            </span>
            <span className="flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5} className="text-[#d4af37]" /> Daily Vanishing Stories
            </span>
          </div>
        </div>
      </section>

      {/* Footer note */}
      <footer className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pb-10 text-white/30 text-xs tracking-[0.18em] uppercase flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span>© Lumière Club · Est. 2026</span>
        <div className="flex gap-5">
          <Link to="/privacy" className="hover:text-[#d4af37] transition" data-testid="landing-privacy-link">
            Privacy · 隐私政策
          </Link>
          <Link to="/terms" className="hover:text-[#d4af37] transition" data-testid="landing-terms-link">
            Terms · 服务条款
          </Link>
        </div>
      </footer>
    </div>
  );
}
